// Data Integrity proofs the rail and the platform use.
//
// eddsa-jcs-2022 (did:webvh log entries; did_toolkit.did_webvh.sign_proof):
//   hash = sha256(jcs(proofConfig [+ @context of the document])) || sha256(jcs(document))
//   proofValue = "z" + base58btc(Ed25519 signature over hash)
//
// The platform's "ecdsa-jcs-2019" (credential schemas v02, v03, v07; signer.canonical_message):
//   NOT the W3C cryptosuite algorithm. The issuer key signs ECDSA-P256-SHA256 over
//   the canonical JSON of the credential WITHOUT its proof; proofValue is
//   multibase (z/u/m) of a DER signature. Reproduced here as the platform does it.
import { ed25519 } from "@noble/curves/ed25519";
import { p256 } from "@noble/curves/p256";
import { sha256 } from "@noble/hashes/sha256";
import { b58, b58Decode, concat, multibaseDecode } from "./encoding";
import { canonical } from "./jcs";
import type { PublicKey } from "./keys";

export const EDDSA_JCS_2022 = "eddsa-jcs-2022";

function proofHash(unsecured: Record<string, unknown>, config: Record<string, unknown>): Uint8Array {
  return concat(sha256(canonical(config)), sha256(canonical(unsecured)));
}

export interface ProofOptions { verificationMethod: string; created: string; proofPurpose?: string }

export async function signEddsaJcs2022(unsecured: Record<string, unknown>,
                                       sign: (m: Uint8Array) => Promise<Uint8Array>,
                                       opts: ProofOptions): Promise<Record<string, unknown>> {
  const config: Record<string, unknown> = {
    type: "DataIntegrityProof", cryptosuite: EDDSA_JCS_2022,
    verificationMethod: opts.verificationMethod, proofPurpose: opts.proofPurpose ?? "assertionMethod",
    created: opts.created,
  };
  const hashed = { ...config, ...("@context" in unsecured ? { "@context": unsecured["@context"] } : {}) };
  const sig = await sign(proofHash(unsecured, hashed));
  return { ...config, proofValue: "z" + b58(sig) };
}

export function verifyEddsaJcs2022(unsecured: Record<string, unknown>, proof: any, pk: PublicKey): boolean {
  if (!proof || typeof proof !== "object" || pk.kind !== "Ed25519") return false;
  if (proof.type !== "DataIntegrityProof" || proof.cryptosuite !== EDDSA_JCS_2022) return false;
  const value = proof.proofValue;
  if (typeof value !== "string" || !value.startsWith("z")) return false;
  const { proofValue: _omit, ...config } = proof;
  if ("@context" in unsecured) config["@context"] = unsecured["@context"];
  try {
    return ed25519.verify(b58Decode(value.slice(1)), proofHash(unsecured, config), pk.raw);
  } catch { return false; }
}

/** The platform's issuer proof on v02/v03/v07 credentials. */
export function verifyPlatformEcdsaJcs2019(credential: Record<string, any>, pk: PublicKey): boolean {
  const proof = credential.proof;
  if (!proof || proof.type !== "DataIntegrityProof" || proof.cryptosuite !== "ecdsa-jcs-2019") return false;
  if (pk.kind !== "P-256") return false;
  const { proof: _p, ...unsigned } = credential;
  try {
    const der = multibaseDecode(String(proof.proofValue));
    return p256.verify(der, sha256(canonical(unsigned)), pk.raw, { lowS: false, prehash: false, format: "der" });
  } catch { return false; }
}

/** Sign as the platform does (tests and the mock issuer only). */
export function signPlatformEcdsaJcs2019(credential: Record<string, any>, priv: Uint8Array,
                                         verificationMethod: string, created: string): Record<string, any> {
  const { proof: _p, ...unsigned } = credential;
  const der = p256.sign(sha256(canonical(unsigned)), priv, { prehash: false }).toDERRawBytes();
  return { ...unsigned, proof: { type: "DataIntegrityProof", cryptosuite: "ecdsa-jcs-2019",
    verificationMethod, proofPurpose: "assertionMethod", created, proofValue: "z" + b58(der) } };
}
